import React, { useState, useEffect, useCallback } from 'react';
import { scriptsService, DEFAULT_SCRIPTS } from '../../services/scriptsService';
import ScriptRenderer from '../incidence-steps/script-engine/ScriptRenderer';

// ── Constantes ────────────────────────────────────────────────────────────────

const TIPOS_SCRIPT = [
  { id: 'sat',          nombre: 'SAT',          emoji: '🔧', desc: 'Averías técnicas' },
  { id: 'mantenimiento',nombre: 'Mantenimiento', emoji: '🛠️', desc: 'Limpieza y revisión' },
  { id: 'presupuesto',  nombre: 'Presupuesto',   emoji: '💰', desc: 'Consultas y precios' },
  { id: 'instalacion',  nombre: 'Instalación',   emoji: '🏗️', desc: 'Nuevas instalaciones' },
];

const TIPOS_PREGUNTA = [
  { id: 'chips',      label: 'Chips',       desc: 'Botones seleccionables (uno o varios)' },
  { id: 'quick-date', label: 'Fecha rápida',desc: 'Opciones relativas + fecha exacta' },
  { id: 'opciones',   label: 'Opciones',    desc: 'Botones con descripción detallada' },
  { id: 'texto',      label: 'Texto libre', desc: 'Campo de escritura libre' },
];

const COLOR_OPTIONS = ['gray', 'yellow', 'orange', 'red', 'green', 'blue'];

const EMPTY_PREGUNTA = {
  id: '', orden: 99, pregunta: '', tipo: 'chips', multiple: false,
  campo: '', obligatorio: true, activo: true, generarDescripcion: false,
  placeholder: '', multilinea: false, opciones: [], mostrarSi: null,
};

const EMPTY_OPCION = { id: '', label: '', emoji: '', texto: '', valor: '', desc: '', color: 'gray', esTextoLibre: false, esDatePicker: false };

// ── Helpers ───────────────────────────────────────────────────────────────────

const slugify = (str) =>
  (str || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^a-z0-9]/g, '_').replace(/_+/g, '_').slice(0, 40);

// ── Componente: editor de una opción ─────────────────────────────────────────
const OpcionEditor = ({ opcion, onChange, onDelete, tipoPregunta }) => (
  <div className="flex items-start gap-2 p-2 bg-gray-50 rounded-xl border border-gray-200">
    <div className="grid grid-cols-2 gap-1.5 flex-1 min-w-0">
      <input className="crm-input text-xs" placeholder="Emoji (🔧)" value={opcion.emoji || ''} maxLength={4}
        onChange={e => onChange({ ...opcion, emoji: e.target.value })} />
      <input className="crm-input text-xs" placeholder="Etiqueta *" value={opcion.label || ''}
        onChange={e => onChange({ ...opcion, label: e.target.value, id: opcion.id || slugify(e.target.value) })} />
      {(tipoPregunta === 'chips' || tipoPregunta === 'opciones') && (
        <input className="crm-input text-xs col-span-2" placeholder="Valor guardado en formData (ej: si, media, ariston)"
          value={opcion.valor || ''} onChange={e => onChange({ ...opcion, valor: e.target.value })} />
      )}
      {tipoPregunta === 'chips' && (
        <input className="crm-input text-xs col-span-2" placeholder='Texto para descripción (ej: "El equipo no enciende")'
          value={opcion.texto || ''} onChange={e => onChange({ ...opcion, texto: e.target.value })} />
      )}
      {tipoPregunta === 'opciones' && (
        <input className="crm-input text-xs col-span-2" placeholder='Descripción breve (ej: "Puede esperar unos días")'
          value={opcion.desc || ''} onChange={e => onChange({ ...opcion, desc: e.target.value })} />
      )}
      {tipoPregunta === 'opciones' && (
        <div className="col-span-2 flex items-center gap-2">
          <span className="text-xs text-gray-500">Color:</span>
          <div className="flex gap-1">
            {COLOR_OPTIONS.map(c => (
              <button key={c} type="button" onClick={() => onChange({ ...opcion, color: c })}
                className={`w-5 h-5 rounded-full border-2 transition-all ${opcion.color === c ? 'border-gray-800 scale-110' : 'border-gray-300'}`}
                style={{ background: { gray: '#9ca3af', yellow: '#fbbf24', orange: '#fb923c', red: '#ef4444', green: '#10b981', blue: '#3b82f6' }[c] }}
              />
            ))}
          </div>
        </div>
      )}
      {tipoPregunta === 'quick-date' && (
        <label className="col-span-2 flex items-center gap-2 text-xs text-gray-600 cursor-pointer">
          <input type="checkbox" checked={!!opcion.esDatePicker}
            onChange={e => onChange({ ...opcion, esDatePicker: e.target.checked })} />
          Es selector de fecha exacta
        </label>
      )}
      <label className="col-span-2 flex items-center gap-2 text-xs text-gray-600 cursor-pointer">
        <input type="checkbox" checked={!!opcion.esTextoLibre}
          onChange={e => onChange({ ...opcion, esTextoLibre: e.target.checked })} />
        Es opción "Otro..." (muestra campo libre)
      </label>
    </div>
    <button type="button" onClick={onDelete}
      className="text-red-400 hover:text-red-600 p-1 rounded-lg hover:bg-red-50 flex-shrink-0 transition-colors">
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
      </svg>
    </button>
  </div>
);

// ── Componente: editor inline de una pregunta ─────────────────────────────────
const PreguntaEditor = ({ pregunta, onChange, preguntas }) => {
  const addOpcion = () => {
    const order = (pregunta.opciones?.length || 0) + 1;
    onChange({ ...pregunta, opciones: [...(pregunta.opciones || []), { ...EMPTY_OPCION, id: `opcion_${order}` }] });
  };

  const updateOpcion = (idx, val) => {
    const next = [...(pregunta.opciones || [])];
    next[idx] = val;
    onChange({ ...pregunta, opciones: next });
  };

  const deleteOpcion = (idx) => {
    onChange({ ...pregunta, opciones: (pregunta.opciones || []).filter((_, i) => i !== idx) });
  };

  // Preguntas disponibles para condicional (excluye la propia)
  const preguntasDisponibles = (preguntas || []).filter(p => p.id !== pregunta.id);

  return (
    <div className="space-y-4">
      {/* Texto de la pregunta */}
      <div>
        <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">Pregunta *</label>
        <input className="crm-input" placeholder="¿Qué problema reporta el cliente?"
          value={pregunta.pregunta} onChange={e => onChange({ ...pregunta, pregunta: e.target.value,
            id: pregunta.id || slugify(e.target.value) })} />
      </div>

      {/* ID y campo */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">ID interno</label>
          <input className="crm-input font-mono text-xs" placeholder="sintoma"
            value={pregunta.id} onChange={e => onChange({ ...pregunta, id: e.target.value })} />
        </div>
        <div>
          <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">Campo formData *</label>
          <input className="crm-input font-mono text-xs" placeholder="descripcionFallo"
            value={pregunta.campo} onChange={e => onChange({ ...pregunta, campo: e.target.value })} />
        </div>
      </div>

      {/* Tipo */}
      <div>
        <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">Tipo de pregunta</label>
        <div className="grid grid-cols-2 gap-2">
          {TIPOS_PREGUNTA.map(t => (
            <button key={t.id} type="button" onClick={() => onChange({ ...pregunta, tipo: t.id })}
              className={`text-left px-3 py-2 rounded-xl border-2 transition-all ${
                pregunta.tipo === t.id ? 'border-red-500 bg-red-50' : 'border-gray-200 bg-white hover:border-gray-300'
              }`}>
              <p className="text-xs font-bold text-gray-800">{t.label}</p>
              <p className="text-[10px] text-gray-500 mt-0.5">{t.desc}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Opciones según tipo */}
      {pregunta.tipo === 'chips' && (
        <div className="flex gap-4">
          <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
            <input type="checkbox" checked={!!pregunta.multiple}
              onChange={e => onChange({ ...pregunta, multiple: e.target.checked })} />
            Selección múltiple
          </label>
          <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
            <input type="checkbox" checked={!!pregunta.generarDescripcion}
              onChange={e => onChange({ ...pregunta, generarDescripcion: e.target.checked })} />
            Generar descripción automática
          </label>
        </div>
      )}

      {pregunta.tipo === 'texto' && (
        <div className="flex gap-4">
          <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
            <input type="checkbox" checked={!!pregunta.multilinea}
              onChange={e => onChange({ ...pregunta, multilinea: e.target.checked })} />
            Multilínea (textarea)
          </label>
          <div className="flex-1">
            <input className="crm-input text-xs" placeholder="Texto de ayuda (placeholder)"
              value={pregunta.placeholder || ''} onChange={e => onChange({ ...pregunta, placeholder: e.target.value })} />
          </div>
        </div>
      )}

      {/* Opciones de respuesta */}
      {['chips', 'quick-date', 'opciones'].includes(pregunta.tipo) && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Opciones de respuesta</label>
            <button type="button" onClick={addOpcion}
              className="text-xs font-semibold text-red-600 hover:text-red-800 flex items-center gap-1">
              + Añadir opción
            </button>
          </div>
          <div className="space-y-2">
            {(pregunta.opciones || []).map((op, idx) => (
              <OpcionEditor key={idx} opcion={op} tipoPregunta={pregunta.tipo}
                onChange={val => updateOpcion(idx, val)} onDelete={() => deleteOpcion(idx)} />
            ))}
            {(pregunta.opciones || []).length === 0 && (
              <p className="text-xs text-gray-400 text-center py-3 bg-gray-50 rounded-xl border border-dashed">
                Sin opciones — pulsa "+ Añadir opción"
              </p>
            )}
          </div>
        </div>
      )}

      {/* Condicional: mostrar esta pregunta solo si... */}
      <div>
        <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">
          Mostrar esta pregunta solo si...
        </label>
        <div className="grid grid-cols-2 gap-2">
          <select className="crm-input text-sm"
            value={pregunta.mostrarSi?.preguntaId || ''}
            onChange={e => {
              if (!e.target.value) { onChange({ ...pregunta, mostrarSi: null }); return; }
              onChange({ ...pregunta, mostrarSi: { ...pregunta.mostrarSi, preguntaId: e.target.value, valorId: pregunta.mostrarSi?.valorId || '' } });
            }}>
            <option value="">Siempre visible</option>
            {preguntasDisponibles.map(p => (
              <option key={p.id} value={p.id}>{p.pregunta.slice(0, 40)}</option>
            ))}
          </select>
          {pregunta.mostrarSi?.preguntaId && (
            <input className="crm-input text-sm font-mono" placeholder="valorId (ej: si, ariston)"
              value={pregunta.mostrarSi.valorId || ''}
              onChange={e => onChange({ ...pregunta, mostrarSi: { ...pregunta.mostrarSi, valorId: e.target.value } })} />
          )}
        </div>
      </div>

      {/* Obligatorio / Activo */}
      <div className="flex gap-4 pt-1 border-t border-gray-100">
        <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
          <input type="checkbox" checked={!!pregunta.obligatorio}
            onChange={e => onChange({ ...pregunta, obligatorio: e.target.checked })} />
          Obligatorio
        </label>
        <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
          <input type="checkbox" checked={pregunta.activo !== false}
            onChange={e => onChange({ ...pregunta, activo: e.target.checked })} />
          Activo
        </label>
      </div>
    </div>
  );
};

// ── Panel principal del editor de un script ───────────────────────────────────
const ScriptPanel = ({ tipoId, onBack }) => {
  const [script, setScript] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [saved, setSaved]     = useState(false);
  const [error, setError]     = useState('');
  const [selectedPreguntaId, setSelectedPreguntaId] = useState(null);
  const [previewMode, setPreviewMode] = useState(false);
  const [previewFormData, setPreviewFormData] = useState({ tipoIncidencia: tipoId });

  useEffect(() => {
    setLoading(true);
    scriptsService.getScript(tipoId)
      .then(s => { setScript(s); setLoading(false); })
      .catch(() => { setError('Error cargando script'); setLoading(false); });
  }, [tipoId]);

  const selectedPregunta = script?.preguntas?.find(p => p.id === selectedPreguntaId) || null;

  const addPregunta = () => {
    const newId = `pregunta_${Date.now()}`;
    const newP = { ...EMPTY_PREGUNTA, id: newId, orden: (script.preguntas?.length || 0) + 1 };
    setScript(s => ({ ...s, preguntas: [...(s.preguntas || []), newP] }));
    setSelectedPreguntaId(newId);
  };

  const updatePregunta = useCallback((updated) => {
    setScript(s => ({
      ...s,
      preguntas: (s.preguntas || []).map(p => p.id === updated.id ? updated : p),
    }));
  }, []);

  const deletePregunta = (id) => {
    if (!window.confirm('¿Eliminar esta pregunta?')) return;
    setScript(s => ({ ...s, preguntas: (s.preguntas || []).filter(p => p.id !== id) }));
    if (selectedPreguntaId === id) setSelectedPreguntaId(null);
  };

  const moveUp = (idx) => {
    if (idx === 0) return;
    setScript(s => {
      const arr = [...(s.preguntas || [])];
      [arr[idx - 1], arr[idx]] = [arr[idx], arr[idx - 1]];
      arr.forEach((p, i) => { p.orden = i + 1; });
      return { ...s, preguntas: arr };
    });
  };

  const moveDown = (idx) => {
    setScript(s => {
      const arr = [...(s.preguntas || [])];
      if (idx >= arr.length - 1) return s;
      [arr[idx], arr[idx + 1]] = [arr[idx + 1], arr[idx]];
      arr.forEach((p, i) => { p.orden = i + 1; });
      return { ...s, preguntas: arr };
    });
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      await scriptsService.saveScript(tipoId, script);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('¿Restaurar el script a los valores por defecto? Se perderán todos los cambios.')) return;
    setSaving(true);
    try {
      const s = await scriptsService.resetToDefault(tipoId);
      setScript(s);
      setSelectedPreguntaId(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center py-20 text-gray-400">
      <div className="w-6 h-6 border-2 border-gray-300 border-t-red-500 rounded-full animate-spin mr-3" />
      Cargando...
    </div>
  );

  if (!script) return (
    <div className="text-center py-20 text-red-600">{error || 'Script no encontrado'}</div>
  );

  const tipo = TIPOS_SCRIPT.find(t => t.id === tipoId);

  return (
    <div className="space-y-4">

      {/* ── Header del script ── */}
      <div className="bg-white rounded-2xl border shadow-sm p-4">
        <div className="flex items-center gap-3">
          <button onClick={onBack}
            className="p-1.5 rounded-xl hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <div className="text-2xl">{tipo?.emoji}</div>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <h2 className="text-base font-bold text-gray-900">{script.nombre}</h2>
              <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">v{script.version}</span>
            </div>
            <p className="text-xs text-gray-400">{script.preguntas?.length || 0} preguntas · {script.descripcion}</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setPreviewMode(m => !m)}
              className={`px-3 py-1.5 rounded-xl text-xs font-semibold border transition-all ${
                previewMode ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'
              }`}>
              {previewMode ? '✏️ Editar' : '👁️ Vista previa'}
            </button>
            <button onClick={handleReset}
              className="px-3 py-1.5 rounded-xl text-xs font-semibold bg-white text-gray-500 border border-gray-200 hover:text-red-600 hover:border-red-200 transition-all">
              ↺ Restablecer
            </button>
            <button onClick={handleSave} disabled={saving}
              className={`px-4 py-1.5 rounded-xl text-xs font-bold transition-all ${
                saved ? 'bg-emerald-600 text-white' : 'bg-red-600 hover:bg-red-700 text-white disabled:opacity-50'
              }`}>
              {saving ? 'Guardando…' : saved ? '✓ Guardado' : '💾 Guardar'}
            </button>
          </div>
        </div>
        {error && (
          <div className="mt-2 bg-red-50 border border-red-200 rounded-xl px-3 py-2 text-xs text-red-700">{error}</div>
        )}
      </div>

      {/* ── Vista previa ── */}
      {previewMode ? (
        <div className="bg-white rounded-2xl border shadow-sm overflow-hidden">
          <div className="bg-blue-50 border-b px-4 py-2 flex items-center gap-2">
            <span className="text-xs font-bold text-blue-700 uppercase tracking-wide">Vista previa del operador</span>
          </div>
          <ScriptRenderer
            formData={{ ...previewFormData, tipoIncidencia: tipoId }}
            updateFormData={upd => setPreviewFormData(prev => ({ ...prev, ...upd }))}
          />
        </div>
      ) : (
        /* ── Editor ── */
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

          {/* Lista de preguntas */}
          <div className="bg-white rounded-2xl border shadow-sm overflow-hidden">
            <div className="bg-gray-50 border-b px-4 py-2.5 flex items-center justify-between">
              <span className="text-xs font-bold text-gray-500 uppercase tracking-wide">
                Preguntas ({script.preguntas?.length || 0})
              </span>
              <button onClick={addPregunta}
                className="text-xs font-bold text-red-600 hover:text-red-800 flex items-center gap-1 transition-colors">
                + Añadir pregunta
              </button>
            </div>

            <div className="divide-y divide-gray-50">
              {(script.preguntas || []).map((p, idx) => (
                <div
                  key={p.id}
                  onClick={() => setSelectedPreguntaId(selectedPreguntaId === p.id ? null : p.id)}
                  className={`flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors group ${
                    selectedPreguntaId === p.id ? 'bg-red-50 border-l-4 border-red-500' : 'hover:bg-gray-50'
                  } ${!p.activo ? 'opacity-50' : ''}`}
                >
                  {/* Orden */}
                  <div className="flex flex-col gap-0.5 flex-shrink-0">
                    <button type="button" onClick={e => { e.stopPropagation(); moveUp(idx); }}
                      disabled={idx === 0}
                      className="w-5 h-4 flex items-center justify-center text-gray-300 hover:text-gray-600 disabled:opacity-20 transition-colors">
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 15l7-7 7 7" />
                      </svg>
                    </button>
                    <button type="button" onClick={e => { e.stopPropagation(); moveDown(idx); }}
                      disabled={idx === (script.preguntas?.length || 1) - 1}
                      className="w-5 h-4 flex items-center justify-center text-gray-300 hover:text-gray-600 disabled:opacity-20 transition-colors">
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                  </div>

                  {/* Número */}
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                    selectedPreguntaId === p.id ? 'bg-red-500 text-white' : 'bg-gray-200 text-gray-600'
                  }`}>{idx + 1}</div>

                  {/* Texto */}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-800 truncate">{p.pregunta || '(sin texto)'}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-[10px] text-gray-400 font-mono">{p.tipo}</span>
                      {p.campo && <span className="text-[10px] text-gray-400">→ {p.campo}</span>}
                      {!p.obligatorio && <span className="text-[10px] text-amber-500">opcional</span>}
                      {p.mostrarSi && <span className="text-[10px] text-blue-400">condicional</span>}
                    </div>
                  </div>

                  {/* Acciones */}
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button type="button" onClick={e => { e.stopPropagation(); deletePregunta(p.id); }}
                      className="p-1 rounded-lg text-gray-300 hover:text-red-500 hover:bg-red-50 transition-colors">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </div>
              ))}

              {(script.preguntas || []).length === 0 && (
                <div className="text-center py-10 text-gray-400">
                  <p className="text-sm">Sin preguntas</p>
                  <p className="text-xs mt-1">Pulsa "+ Añadir pregunta" para empezar</p>
                </div>
              )}
            </div>
          </div>

          {/* Editor de pregunta seleccionada */}
          <div className="bg-white rounded-2xl border shadow-sm overflow-hidden">
            {selectedPregunta ? (
              <>
                <div className="bg-gray-50 border-b px-4 py-2.5 flex items-center justify-between">
                  <span className="text-xs font-bold text-gray-500 uppercase tracking-wide">
                    Editar pregunta
                  </span>
                  <button onClick={() => setSelectedPreguntaId(null)}
                    className="text-xs text-gray-400 hover:text-gray-600 transition-colors">
                    ✕ Cerrar
                  </button>
                </div>
                <div className="p-4 overflow-y-auto max-h-[600px]">
                  <PreguntaEditor
                    pregunta={selectedPregunta}
                    onChange={updatePregunta}
                    preguntas={script.preguntas}
                  />
                </div>
              </>
            ) : (
              <div className="flex flex-col items-center justify-center h-full min-h-[300px] text-gray-400 p-6">
                <div className="text-4xl mb-3">✏️</div>
                <p className="text-sm font-medium">Selecciona una pregunta</p>
                <p className="text-xs mt-1 text-center">Haz clic en una pregunta de la lista para editarla</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ── Vista principal ───────────────────────────────────────────────────────────
const ScriptEditorView = () => {
  const [selectedTipo, setSelectedTipo] = useState(null);

  if (selectedTipo) {
    return (
      <div className="max-w-5xl mx-auto px-4 py-4">
        <ScriptPanel tipoId={selectedTipo} onBack={() => setSelectedTipo(null)} />
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 py-6 space-y-6">

      <div>
        <h1 className="text-2xl font-bold text-gray-900">Scripts de atención al cliente</h1>
        <p className="text-sm text-gray-500 mt-1">
          Configura las preguntas que verán los operadores al crear cada tipo de orden.
          Los cambios se aplican en tiempo real sin necesidad de publicar código.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {TIPOS_SCRIPT.map(tipo => (
          <button
            key={tipo.id}
            onClick={() => setSelectedTipo(tipo.id)}
            className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5 text-left hover:border-red-300 hover:shadow-md transition-all group"
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="w-12 h-12 rounded-2xl bg-red-50 flex items-center justify-center text-2xl group-hover:bg-red-100 transition-colors">
                {tipo.emoji}
              </div>
              <div>
                <h3 className="font-bold text-gray-900">{tipo.nombre}</h3>
                <p className="text-xs text-gray-400">{tipo.desc}</p>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-400">
                {DEFAULT_SCRIPTS[tipo.id]?.preguntas?.length || 0} preguntas por defecto
              </span>
              <span className="text-xs font-semibold text-red-600 group-hover:text-red-700">
                Editar →
              </span>
            </div>
          </button>
        ))}
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4">
        <h3 className="text-sm font-bold text-blue-800 mb-1">¿Cómo funciona?</h3>
        <ul className="text-xs text-blue-700 space-y-1 list-disc list-inside">
          <li>Cada tipo de servicio tiene su propio script con preguntas configurables</li>
          <li>El operador responde con un clic — sin escribir en el 90% de los casos</li>
          <li>Los cambios se guardan en Firebase y se aplican al instante para todos los operadores</li>
          <li>Puedes añadir, eliminar, reordenar preguntas y configurar lógica condicional</li>
          <li>La vista previa muestra exactamente lo que verá el operador</li>
        </ul>
      </div>

      <style>{`
        .crm-input {
          width: 100%;
          padding: 0.5rem 0.75rem;
          border: 1.5px solid #e5e7eb;
          border-radius: 0.5rem;
          font-size: 0.875rem;
          outline: none;
          transition: border-color 0.15s;
          background: white;
        }
        .crm-input:focus { border-color: #dc2626; }
        select.crm-input { appearance: auto; }
      `}</style>
    </div>
  );
};

export default ScriptEditorView;
